import * as React from "react";
import AppShell from "./AppShell";
import Progress from "./Progress";
import { ConversationMessage, MessageFile, ModelInfo, QuickAction, EditPayload, ChangeProposal, ChangeProposalState, UserProfile, ModelChoice, UserSettings, AttachmentRef, FulltextFileRef, OwuiFolder, ApplyMode } from "@lupp/shared";
import { initialMessages, quickActions } from "../mocks/chat";
import { sendMessage, ThinkingUpdate } from "../services/assistant";
import { authenticate, listenForReAuth, onSocketReconnect, subscribeUserSettings } from "../services/auth";
import { fetchModels } from "../services/models";
import { loadChat, listChats, editMessage, deleteMessage, persistChangeStates, subscribeChatSaved, subscribeChatTitle, subscribeTurnPersisted, moveChat } from "../services/chats";
import { listFolders, getFolderChats, moveFolder } from "../services/folders";
import { folderPath } from "../services/folderTree";
import { historyCache } from "../services/historyCache";
import { showModal } from "../services/modal";
import ModalHost from "./ModalHost";
import { TrashIcon } from "./icons";
import { toNodeMap, buildActivePath, siblingsOf, leafOf, type NodeMap } from "../services/conversationTree";
import { loadPersistedSession, persistChatId, persistSelectedModel, persistWebSearch, persistFolderId, persistApplyMode, getGlobalDefaultModel } from "../services/sessionState";
import { fmtTimestamp } from "../utils/time";
import { buildWordSkill } from "../services/wordContext";
import { subscribeClientRequests, captureSelectionForTurn } from "../services/documentBridge";
import { applyChange, scrollToParagraph } from "../services/wordApply";

export interface AppProps {
  title: string;
  isOfficeInitialized: boolean;
}

export interface AppState {
  draft: string;
  isSending: boolean;
  messages: ConversationMessage[];
  selectedModelId: string;
  models: ModelChoice[];
  // Server-Feedback-Akkumulator: was seit dem letzten Turn übernommen/abgelehnt wurde (für die KI).
  appliedChangeIds: string[];
  rejectedChangeIds: string[];
  user: UserProfile | null;
  userSettings: UserSettings | null;
  chatId?: string; // OWUI-Chat der laufenden Konversation (vom Server angelegt)
  webSearch: boolean; // Websuche aktiv — pro Dokument persistiert
  treeNodes?: NodeMap; // OWUI-Konversationsbaum eines geladenen Chats (für Fork-Navigation)
  branchLeafId?: string | null; // aktives Blatt im Baum
  scrollTarget?: { id: string | null; seq: number }; // gezieltes Scroll-Ziel (Switch/Delete)
  // Nur EINE Sidebar gleichzeitig offen: Chat-Verlauf ODER Änderungsvorschläge (oder keine).
  openSidebar: "history" | "proposals" | null;
  proposalsMessageId: string | null; // welche Assistant-Nachricht die Vorschlags-Sidebar zeigt
  docked: boolean; // Pane breit genug (≥600px), um die Sidebar daneben anzudocken
  folders: OwuiFolder[]; // OWUI-Ordner ("Projekte") — für Breadcrumb + Sidebar-Baum
  activeFolderId: string | null; // aktueller Projekt-Scope (neue Chats landen hier)
  historyRefreshKey: number; // bumped → Sidebar lädt die Chat-Liste neu (neuer Chat / Turn-Ende)
  currentChatTitle: string; // Titel des offenen Chats (Topbar-Breadcrumb)
  applyMode: ApplyMode; // wie angenommene Änderungen geschrieben werden (pro Dokument)
}

export default class App extends React.Component<AppProps, AppState> {
  private abortController: AbortController | null = null;
  private stopReAuth: (() => void) | null = null;
  private stopReconnectAuth: (() => void) | null = null;
  private stopChatSaved: (() => void) | null = null;
  private stopChatTitle: (() => void) | null = null;
  private stopUserSettings: (() => void) | null = null;
  private stopClientRequests: (() => void) | null = null;
  private stopTurnPersisted: (() => void) | null = null;
  private currentThinkingId: string | null = null; // Ziel-Nachricht für eingehende Vorschläge im Turn

  constructor(props) {
    super(props);
    this.state = {
      draft: "",
      isSending: false,
      messages: initialMessages,
      selectedModelId: "",
      models: [],
      appliedChangeIds: [],
      rejectedChangeIds: [],
      user: null,
      userSettings: null,
      webSearch: false,
      openSidebar: null,
      proposalsMessageId: null,
      docked: false,
      folders: [],
      activeFolderId: null,
      historyRefreshKey: 0,
      currentChatTitle: "",
      applyMode: "with_tracked_changes",
    };
  }

  private bumpHistory = (): void => this.setState((s) => ({ historyRefreshKey: s.historyRefreshKey + 1 }));

  componentDidMount(): void {
    if (this.props.isOfficeInitialized) {
      this.doAuthenticate();
    }
  }

  componentDidUpdate(prevProps: AppProps): void {
    if (!prevProps.isOfficeInitialized && this.props.isOfficeInitialized) {
      this.doAuthenticate();
    }
  }

  componentWillUnmount(): void {
    this.stopReAuth?.();
    this.stopReconnectAuth?.();
    this.stopChatSaved?.();
    this.stopChatTitle?.();
    this.stopUserSettings?.();
    this.stopClientRequests?.();
    this.stopTurnPersisted?.();
  }

  private doAuthenticate(): void {
    // Initial: authenticate, fetch models, then restore the document's persisted session.
    authenticate()
      .then((user) => {
        this.setState({ user });
        return fetchModels();
      })
      .then((models) => this.restoreSession(models))
      .catch((err: unknown) => console.warn("Auth or model fetch failed:", err));

    // Re-auth: keep the live chat/selection, just refresh the model list. Triggered
    // both by an explicit server auth_required AND by any socket reconnect (a new
    // socket.id has no server-side session, e.g. after a server restart) — without this
    // the session silently has no context (ctx=false) and nothing gets persisted.
    const reauth = (): Promise<void> =>
      authenticate()
        .then((user) => {
          this.setState({ user });
          return fetchModels();
        })
        .then((models) => {
          this.setState((s) => ({
            models,
            selectedModelId: s.selectedModelId || (models[0]?.id ?? ""),
          }));
        })
        .catch((err: unknown) => console.warn("Re-auth failed:", err));
    this.stopReAuth = listenForReAuth(reauth);
    this.stopReconnectAuth = onSocketReconnect(() => void reauth());

    this.stopUserSettings = subscribeUserSettings((userSettings) => {
      this.setState({ userSettings });
    });

    // Answer server get_document / get_selection round-trips for the edit flow.
    this.stopClientRequests = subscribeClientRequests();

    // After a fork-creating turn (edit-resend) the server pushes the fresh tree → rebuild
    // it so the branch switcher (with the updated count) shows.
    this.stopChatSaved = subscribeChatSaved(({ chatId, messages, currentId }) => {
      if (chatId !== this.state.chatId || messages.length === 0) return;
      this.setLoadedChat(messages, currentId);
      // The rebuild replaces the optimistic message (thinkingId) with the persisted, real-id
      // messages. Re-point the open proposal sidebar to this turn's rebuilt assistant message
      // (currentId) — it carries the server-persisted changeProposals — so it doesn't blank out.
      if (this.currentThinkingId && this.state.proposalsMessageId === this.currentThinkingId && currentId) {
        this.currentThinkingId = currentId;
        this.setState({ proposalsMessageId: currentId });
      }
    });

    // Nach einem normalen Turn: die ECHTE server-vergebene Message-ID als `serverId` auf der
    // optimistischen Nachricht ablegen (die `id`/thinkingId bleibt — kein Remount, keine
    // Sidebar-Störung). Über `serverId` korreliert update_change_states den applied-Status.
    this.stopTurnPersisted = subscribeTurnPersisted(({ chatId, messageId }) => {
      if (chatId !== this.state.chatId) return;
      const targetId = this.currentThinkingId;
      if (!targetId) return;
      this.setState(
        (s) => {
          if (!s.messages.some((m) => m.id === targetId)) return null;
          return { messages: s.messages.map((m) => (m.id === targetId ? { ...m, serverId: messageId } : m)) };
        },
        () => {
          // Falls vor der Übernahme schon Vorschläge aufgelöst wurden: jetzt serverseitig sichern.
          const msg = this.state.messages.find((m) => m.id === targetId);
          if (this.state.chatId && msg?.changeStates && Object.keys(msg.changeStates).length > 0) {
            persistChangeStates(this.state.chatId, messageId, msg.changeStates);
          }
        },
      );
    });

    // A new chat's generated title is now persisted → refresh the sidebar (with the title)
    // and update the topbar breadcrumb's chat-name tail.
    this.stopChatTitle = subscribeChatTitle(({ chatId, title }) => {
      this.bumpHistory();
      if (chatId === this.state.chatId) this.setState({ currentChatTitle: title });
    });
  }

  /**
   * Restores the document-scoped session after the model list is available:
   * picks the model (persisted per-document → global default → user default → first)
   * and, if this document already has a chat, loads its full history.
   */
  private async restoreSession(models: ModelChoice[]): Promise<void> {
    const persisted = await loadPersistedSession();
    const defaultModel =
      persisted.selectedModelId ??
      getGlobalDefaultModel() ??
      this.state.userSettings?.ui?.defaultModel ??
      models[0]?.id ??
      "";
    this.setState((s) => ({
      models,
      selectedModelId: s.selectedModelId || defaultModel,
      chatId: persisted.chatId ?? s.chatId,
      webSearch: persisted.webSearch ?? false, // per-document, defaults off for new documents
      activeFolderId: persisted.folderId ?? null, // restore project scope like chatId
      applyMode: persisted.applyMode ?? s.applyMode, // per-document apply mode
    }));

    if (persisted.chatId) {
      try {
        const { messages, currentId, title } = await loadChat(persisted.chatId);
        if (messages.length > 0) this.setLoadedChat(messages, currentId, models);
        this.setState({ currentChatTitle: title });
      } catch (err) {
        console.warn("Chat restore failed:", err);
      }
    }

    // Warm the chat-history cache in the background so the sidebar opens instantly
    // (no flicker / loader) and only refreshes the live state silently — including the
    // chats of the folders on the active project path (the deep-linked breadcrumb).
    void this.prefetchHistory(persisted.folderId ?? null);
  }

  private refreshFolders = async (): Promise<void> => {
    try {
      const folders = await listFolders();
      historyCache.setFolders(folders);
      this.setState({ folders });
    } catch {
      /* folders are non-critical */
    }
  };

  /** Background prefetch (after login): chat list + folders + the active path's folder chats. */
  private prefetchHistory = async (activeFolderId: string | null): Promise<void> => {
    void listChats()
      .then((chats) => historyCache.setChats(chats))
      .catch(() => undefined);
    let folders: OwuiFolder[] = [];
    try {
      folders = await listFolders();
      historyCache.setFolders(folders);
      this.setState({ folders });
    } catch {
      return; // without folders we can't prefetch the path
    }
    if (activeFolderId) {
      await Promise.all(
        folderPath(folders, activeFolderId).map((f) =>
          getFolderChats(f.id)
            .then((chats) => historyCache.setFolderChats(f.id, chats))
            .catch(() => undefined),
        ),
      );
    }
  };

  /**
   * Apply a loaded chat: enrich assistant model id → display name/icon, build the tree,
   * show the active path (root→currentId) with fork indicators.
   */
  private setLoadedChat = (raw: ConversationMessage[], currentId: string | null, models = this.state.models): void => {
    const enriched = raw.map((m) => {
      if (m.role !== "assistant" || !m.modelName) return m;
      const model = models.find((x) => x.id === m.modelName);
      return model ? { ...m, modelName: model.name, modelImage: model.image } : m;
    });
    const nodes = toNodeMap(enriched);
    this.setState({ messages: buildActivePath(nodes, currentId), treeNodes: nodes, branchLeafId: currentId });
  };

  /** Switch to the prev/next sibling branch at a forked message and re-render that path. */
  handleSwitchBranch = (messageId: string, dir: -1 | 1): void => {
    const nodes = this.state.treeNodes;
    if (!nodes || !nodes[messageId]) return;
    const siblings = siblingsOf(nodes, nodes[messageId]);
    const j = siblings.indexOf(messageId) + dir;
    if (j < 0 || j >= siblings.length) return;
    const newSibling = siblings[j];
    const leaf = leafOf(nodes, newSibling);
    // Scroll to the switched message itself, not to the end of the strand.
    this.setState((s) => ({
      messages: buildActivePath(nodes, leaf),
      branchLeafId: leaf,
      scrollTarget: { id: newSibling, seq: (s.scrollTarget?.seq ?? 0) + 1 },
    }));
  };

  get currentModel(): ModelInfo {
    const { models, selectedModelId } = this.state;
    const entry = models.find((m) => m.id === selectedModelId) ?? models[0];
    if (!entry) return { id: "", name: "Kein Modell", description: "", iconUrl: undefined };
    return {
      id: entry.id,
      name: entry.name,
      description: entry.description ?? entry.name,
      iconUrl: entry.image,
    };
  }

  handleModelSelect = (model: ModelChoice): void => {
    this.setState({ selectedModelId: model.id });
    void persistSelectedModel(model.id); // per-document (client) + global default
  };

  handleDraftChange = (draft: string): void => this.setState({ draft });

  /** Web search is stored per DOCUMENT (sticky across chats of the same doc), not per chat. */
  handleToggleWebSearch = (): void => {
    this.setState(
      (s) => ({ webSearch: !s.webSearch }),
      () => void persistWebSearch(this.state.webSearch),
    );
  };

  handleQuickAction = (action: QuickAction): void => {
    this.setState({ draft: action.prompt }, () => this.handleSend());
  };

  /** Fresh conversation. Keeps the active project scope (new chats stay in the project). */
  handleNewChat = (): void => {
    this.abortController?.abort();
    this.abortController = null;
    this.setState({ draft: "", isSending: false, messages: initialMessages, appliedChangeIds: [], rejectedChangeIds: [], proposalsMessageId: null, openSidebar: null, chatId: undefined, treeNodes: undefined, branchLeafId: undefined, currentChatTitle: "" });
    void persistChatId(undefined); // start a fresh conversation for this document
  };

  /** Enter a project (folder) scope and start a fresh chat there. */
  handleEnterFolder = (folderId: string): void => {
    this.abortController?.abort();
    this.abortController = null;
    this.setState({
      draft: "",
      isSending: false,
      messages: initialMessages,
      appliedChangeIds: [],
      rejectedChangeIds: [],
      proposalsMessageId: null,
      chatId: undefined,
      treeNodes: undefined,
      branchLeafId: undefined,
      activeFolderId: folderId,
      openSidebar: null,
      currentChatTitle: "",
    });
    void persistChatId(undefined);
    void persistFolderId(folderId);
  };

  /** Leave the project scope (the "x" on the topbar breadcrumb) → fresh, unscoped chat. */
  handleClearProject = (): void => {
    this.setState({ activeFolderId: null });
    void persistFolderId(null);
    this.handleNewChat();
  };

  handleProjectCreated = (): void => {
    void this.refreshFolders();
  };

  /** Drag&drop: move a chat into a folder (or out to top level), then refresh the sidebar. */
  handleMoveChat = async (chatId: string, folderId: string | null): Promise<void> => {
    try {
      await moveChat(chatId, folderId);
    } catch (err) {
      console.warn("Move chat failed:", err);
    }
    // If the moved chat is the open one, keep the breadcrumb scope in sync.
    if (chatId === this.state.chatId) {
      this.setState({ activeFolderId: folderId });
      void persistFolderId(folderId);
    }
    this.bumpHistory();
  };

  /** Drag&drop: re-parent a folder (parentId=null ⇒ top level), then refresh the tree. */
  handleMoveFolder = async (folderId: string, parentId: string | null): Promise<void> => {
    try {
      await moveFolder(folderId, parentId);
    } catch (err) {
      console.warn("Move folder failed:", err);
    }
    await this.refreshFolders();
  };

  // Nur eine Sidebar gleichzeitig: History togglen schließt ggf. die Vorschläge und umgekehrt.
  handleToggleHistory = (): void => this.setState((s) => ({ openSidebar: s.openSidebar === "history" ? null : "history" }));
  handleCloseSidebar = (): void => this.setState({ openSidebar: null });
  handleDockedChange = (docked: boolean): void => this.setState({ docked });

  /** Click auf das Changes-Bullet einer Nachricht → Vorschlags-Sidebar mit DEREN Vorschlägen öffnen. */
  handleOpenProposals = (messageId: string): void => {
    this.setState({ proposalsMessageId: messageId, openSidebar: "proposals" });
  };

  /** Load a chat picked from the history sidebar; it becomes this document's conversation.
   *  `folderId` sets the project scope (a folder chat) or clears it (top-level chat). */
  handleSelectChat = async (chatId: string, folderId?: string | null): Promise<void> => {
    if (chatId === this.state.chatId) {
      this.setState({ openSidebar: null, activeFolderId: folderId ?? null });
      void persistFolderId(folderId ?? null);
      return;
    }
    this.abortController?.abort();
    this.abortController = null;
    try {
      const { messages, currentId, title } = await loadChat(chatId);
      this.setState({
        chatId,
        openSidebar: null,
        proposalsMessageId: null,
        isSending: false,
        activeFolderId: folderId ?? null,
        currentChatTitle: title,
        appliedChangeIds: [],
        rejectedChangeIds: [],
      });
      void persistChatId(chatId);
      void persistFolderId(folderId ?? null);
      this.setLoadedChat(messages, currentId);
    } catch (err) {
      console.warn("Chat load failed:", err);
    }
  };

  /**
   * Eingehende Vorschläge werden an die AKTUELL streamende Assistant-Nachricht gehängt
   * (Basis fürs Changes-Bullet + die Sidebar). Auto-Aufklappen NUR wenn angedockt (≥600px);
   * sonst erscheint nur das Bullet (es blinkt beim Auftauchen).
   */
  handleChangeProposals = (payload: EditPayload): void => {
    const targetId = this.currentThinkingId;
    if (!targetId) return;
    this.setState((s) => ({
      messages: s.messages.map((m) =>
        m.id === targetId ? { ...m, changeProposals: [...(m.changeProposals ?? []), ...payload.changes] } : m,
      ),
      proposalsMessageId: targetId,
      openSidebar: s.docked ? "proposals" : s.openSidebar,
    }));
  };

  handleApplyModeChange = (applyMode: ApplyMode): void => {
    this.setState({ applyMode });
    void persistApplyMode(applyMode);
  };

  /**
   * Setzt den Auflösungs-Status eines Vorschlags auf der Nachricht (für UI + Persistenz) und
   * persistiert die VOLLE changeStates-Map serverseitig (über die Message-ID). Trägt die Nachricht
   * noch die optimistische thinkingId (vor `turn_persisted`), ist der Server-Call ein No-op — die
   * ID-Übernahme sichert die Stände dann nach.
   */
  private setChangeState = (messageId: string, changeId: string, state: ChangeProposalState): void => {
    this.setState(
      (s) => ({
        messages: s.messages.map((m) =>
          m.id === messageId ? { ...m, changeStates: { ...(m.changeStates ?? {}), [changeId]: state } } : m,
        ),
      }),
      () => {
        const { chatId } = this.state;
        const msg = this.state.messages.find((m) => m.id === messageId);
        // Korrelation über die echte Server-ID (nach turn_persisted) bzw. nach Reload über die id.
        if (chatId && msg?.changeStates) persistChangeStates(chatId, msg.serverId ?? messageId, msg.changeStates);
      },
    );
  };

  /**
   * Schließt die Vorschlags-Sidebar automatisch, sobald für DIESE Nachricht nichts mehr offen ist.
   * Als funktionaler Updater (statt setState-Callback), damit auch gebündelte Auflösungen
   * („Alle ablehnen"/schnelle Klicks) den akkumulierten State korrekt sehen.
   */
  private autoCloseIfDone = (messageId: string): void => {
    this.setState((s) => {
      if (s.openSidebar !== "proposals" || s.proposalsMessageId !== messageId) return null;
      const msg = s.messages.find((m) => m.id === messageId);
      if (!msg?.changeProposals) return null;
      const pending = msg.changeProposals.filter((p) => !(msg.changeStates ?? {})[p.id]).length;
      return pending === 0 ? { openSidebar: null } : null;
    });
  };

  private proposalOf = (messageId: string, changeId: string): ChangeProposal | undefined =>
    this.state.messages.find((m) => m.id === messageId)?.changeProposals?.find((p) => p.id === changeId);

  handleAcceptProposal = (messageId: string, id: string): void => {
    const proposal = this.proposalOf(messageId, id);
    if (!proposal) return;
    const { applyMode } = this.state;
    applyChange(proposal, applyMode).catch((err: Error) => console.error("Apply failed:", err));
    this.setChangeState(messageId, id, { status: "applied", applyMode });
    this.setState((s) => ({ appliedChangeIds: [...s.appliedChangeIds, id] }));
    this.autoCloseIfDone(messageId);
  };

  handleRejectProposal = (messageId: string, id: string): void => {
    this.setChangeState(messageId, id, { status: "rejected" });
    this.setState((s) => ({ rejectedChangeIds: [...s.rejectedChangeIds, id] }));
    this.autoCloseIfDone(messageId);
  };

  handleScrollToParagraph = (paragraphId: string): void => {
    scrollToParagraph(paragraphId).catch((err: Error) => console.error("Scroll-to-paragraph failed:", err));
  };

  /** Alle noch offenen Vorschläge der Nachricht übernehmen bzw. ablehnen. */
  private pendingOf = (messageId: string): ChangeProposal[] => {
    const m = this.state.messages.find((x) => x.id === messageId);
    if (!m?.changeProposals) return [];
    return m.changeProposals.filter((p) => !(m.changeStates ?? {})[p.id]);
  };

  handleAcceptAllProposals = (messageId: string): void => {
    for (const p of this.pendingOf(messageId)) this.handleAcceptProposal(messageId, p.id);
  };

  handleRejectAllProposals = (messageId: string): void => {
    for (const p of this.pendingOf(messageId)) this.handleRejectProposal(messageId, p.id);
  };

  handleStop = (): void => {
    this.abortController?.abort();
    this.abortController = null;
  };

  handleSendText = (text: string): void => {
    this.setState({ draft: text }, () => this.handleSend([], []));
  };

  handleSend = async (files: MessageFile[] = [], attachments: AttachmentRef[] = [], fulltextFiles: FulltextFileRef[] = []): Promise<void> => {
    const text = this.state.draft.trim();
    if (!text || this.state.isSending) return;
    // When a branch is selected, append UNDER that leaf (not the chat's persisted currentId).
    await this.startTurn({ text, baseMessages: this.state.messages, parentMessageId: this.state.branchLeafId ?? undefined, files, attachments, fulltextFiles });
  };

  /** Save an edited user message — text only, no new branch (persisted in OWUI). */
  handleEditSave = (messageId: string, text: string): void => {
    this.setState((s) => ({
      messages: s.messages.map((m) => (m.id === messageId ? { ...m, content: text } : m)),
      treeNodes: s.treeNodes ? { ...s.treeNodes, [messageId]: { ...s.treeNodes[messageId], content: text } } : s.treeNodes,
    }));
    if (this.state.chatId) void editMessage(this.state.chatId, messageId, text);
  };

  /** Delete a user message + all its children — confirmed via modal; conversation ends at the parent. */
  handleDeleteMessage = (messageId: string): void => {
    showModal({
      title: "Nachricht löschen?",
      message: "Diese Aktion kann nicht rückgängig gemacht werden. Möchten Sie fortfahren?",
      icon: <TrashIcon size={64} />,
      buttons: [
        { label: "Abbrechen", variant: "grey", align: "left" },
        { label: "Bestätigen", variant: "danger", onClick: () => this.doDeleteMessage(messageId) },
      ],
    });
  };

  private doDeleteMessage = (messageId: string): void => {
    const nodes = this.state.treeNodes;
    if (nodes && nodes[messageId]) {
      const parentId = nodes[messageId].parentId ?? null;
      const next: NodeMap = { ...nodes };
      const stack = [messageId]; // remove ONLY this message + its whole subtree
      while (stack.length > 0) {
        const id = stack.pop()!;
        stack.push(...(next[id]?.childrenIds ?? []));
        delete next[id];
      }
      // Detach from the parent (siblings stay!). If sibling branches remain, switch to the
      // last one so they stay reachable; otherwise the conversation ends at the parent.
      let remaining: string[] = [];
      if (parentId && next[parentId]) {
        remaining = (next[parentId].childrenIds ?? []).filter((c) => c !== messageId);
        next[parentId] = { ...next[parentId], childrenIds: remaining };
      }
      const remainingLeaf = remaining.length > 0 ? remaining[remaining.length - 1] : null;
      const newCurrent = remainingLeaf ? leafOf(next, remainingLeaf) : parentId;
      // Land on the remaining sibling on the same level if any; else scroll to the end.
      this.setState((s) => ({
        treeNodes: next,
        branchLeafId: newCurrent,
        messages: buildActivePath(next, newCurrent),
        scrollTarget: { id: remainingLeaf ?? null, seq: (s.scrollTarget?.seq ?? 0) + 1 },
      }));
    } else {
      // Linear (no tree): just trim the display from the deleted message onward.
      const i = this.state.messages.findIndex((m) => m.id === messageId);
      if (i >= 0) this.setState({ messages: this.state.messages.slice(0, i) });
    }
    if (this.state.chatId) deleteMessage(this.state.chatId, messageId);
  };

  /** Resend an edited user message → creates a NEW branch (sibling) + generates a response. */
  handleEditResend = (messageId: string, text: string): void => {
    if (this.state.isSending) return;
    const i = this.state.messages.findIndex((m) => m.id === messageId);
    if (i < 0) return;
    // New user message becomes a sibling of the edited one → its parent is the prior message.
    // For the ROOT message (i===0) the sibling has NO parent → null (explicit root fork), not
    // undefined (which would make the server append under the current leaf instead of forking).
    const parentMessageId = i > 0 ? this.state.messages[i - 1].id : null;
    // Resend creates a new sibling branch; the server emits chat_saved afterwards →
    // we rebuild the tree (switcher with the updated count) on that event.
    void this.startTurn({ text, baseMessages: this.state.messages.slice(0, i), parentMessageId });
  };

  private startTurn = async (opts: {
    text: string;
    baseMessages: ConversationMessage[];
    parentMessageId?: string | null;
    files?: MessageFile[];
    attachments?: AttachmentRef[];
    fulltextFiles?: FulltextFileRef[];
  }): Promise<void> => {
    const { text, baseMessages, parentMessageId } = opts;
    const files = opts.files ?? [];
    const attachments = opts.attachments ?? [];
    const fulltextFiles = opts.fulltextFiles ?? [];
    const { selectedModelId, models, appliedChangeIds, rejectedChangeIds } = this.state;
    if (this.state.isSending) return;
    const chatIdAtStart = this.state.chatId; // distinguishes new-chat vs. follow-up turn

    const userMsg: ConversationMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: text,
      timestampLabel: fmtTimestamp(new Date()),
      files: files.length > 0 ? files : undefined,
      // pin attached knowledge sources to the optimistic bubble too (not just on reload)
      ...(attachments.length > 0 ? { attachments } : {}),
    };

    // selectedModelId is the OWUI model ID — backend resolves to LiteLLM model + system prompt
    const modelEntry = models.find((m) => m.id === selectedModelId) ?? models[0];
    const owuiModelId = modelEntry?.id ?? selectedModelId;

    /* Word-assistant system-prompt suffix (backend prepends the model's OWUI prompt) */
    const wordSkill = buildWordSkill(this.state.user?.name);
    /* snapshot the current selection so get_selection serves it even if focus moves */
    await captureSelectionForTurn();

    /* abort controller for stop button */
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    /* placeholder message shown while thinking; eingehende Vorschläge hängen sich daran */
    const thinkingId = `assistant-thinking-${Date.now()}`;
    this.currentThinkingId = thinkingId;
    const startedAt = Date.now();
    const thinkingPlaceholder: ConversationMessage = {
      id: thinkingId,
      role: "assistant",
      content: "",
      timestampLabel: "" /* filled when response arrives */,
      modelName: modelEntry?.name ?? owuiModelId,
      modelImage: modelEntry?.image,
      thinkingSteps: [],
      isStreaming: true,
      thinkingStartedAt: startedAt,
    };

    this.setState({
      draft: "",
      isSending: true,
      messages: baseMessages.concat(userMsg, thinkingPlaceholder),
      appliedChangeIds: [],
      rejectedChangeIds: [],
      // Continuing the conversation → leave tree/branch mode (linear from here on).
      treeNodes: undefined,
      branchLeafId: undefined,
    });

    /* find the responseId of the last assistant message for context chaining */
    const lastAssistant = [...baseMessages].reverse().find((m) => m.role === "assistant" && m.responseId);
    const previousResponseId = lastAssistant?.responseId;

    sendMessage({
      model: owuiModelId,
      systemPrompt: wordSkill,
      history: baseMessages,
      signal,
      userMessage: text,
      previousResponseId,
      ...(this.state.chatId ? { chatId: this.state.chatId } : {}),
      ...(appliedChangeIds.length > 0 ? { appliedChangeIds } : {}),
      ...(rejectedChangeIds.length > 0 ? { rejectedChangeIds } : {}),
      ...(attachments.length > 0 ? { attachments } : {}),
      ...(fulltextFiles.length > 0 ? { fulltextFiles } : {}),
      ...(this.state.webSearch ? { webSearch: true } : {}),
      ...(parentMessageId !== undefined ? { parentMessageId } : {}),
      ...(this.state.activeFolderId && !this.state.chatId ? { folderId: this.state.activeFolderId } : {}),
      onChatCreated: (chatId: string): void => {
        this.setState({ chatId });
        void persistChatId(chatId); // remember this document's conversation (client-side)
        // NB: don't refresh the sidebar yet — the title isn't generated until the turn is
        // persisted server-side; we refresh on the `chat_title` event instead.
      },
      onChangeProposals: this.handleChangeProposals,
      onThinking: (update: ThinkingUpdate): void => {
        this.setState((s) => ({
          messages: s.messages.map((m) =>
            m.id === thinkingId ? { ...m, thinkingSteps: [...(m.thinkingSteps ?? []), update.text] } : m
          ),
        }));
      },
      onMessageDelta: (textSoFar: string): void => {
        this.setState((s) => ({
          messages: s.messages.map((m) =>
            m.id === thinkingId ? { ...m, content: textSoFar } : m
          ),
        }));
      },
      onSources: (sources): void => {
        // Append in order across multiple search_web calls — positional integrity so the
        // inline [n] markers map to message.sources[n-1]. Visual de-duplication happens
        // only at display time (by file/hash), not here.
        this.setState((s) => ({
          messages: s.messages.map((m) =>
            m.id === thinkingId ? { ...m, sources: [...(m.sources ?? []), ...sources] } : m,
          ),
        }));
      },
    })
      .then((res) => {
        const durationMs = Date.now() - startedAt;
        this.setState((s) => ({
          isSending: false,
          messages: s.messages.map((m) =>
            m.id === thinkingId
              ? {
                  ...m,
                  content: res.text,
                  timestampLabel: fmtTimestamp(new Date()),
                  thinkingSteps: res.thinkingSteps,
                  isStreaming: false,
                  thinkingDurationMs: durationMs,
                  // keep the sources accumulated live via onSources (...m preserves them)
                  responseId: res.responseId,
                }
              : m
          ),
        }));
        // For follow-up turns in an existing chat, refresh the list (updated_at order /
        // title may change). New chats refresh via the `chat_title` event (title-ready).
        if (chatIdAtStart) this.bumpHistory();
      })
      .catch((err) => {
        const durationMs = Date.now() - startedAt;
        const isAbort = err?.name === "AbortError";
        if (!isAbort) console.error("Assistant error:", err);
        this.abortController = null;
        this.setState((s) => ({
          isSending: false,
          messages: s.messages.map((m) =>
            m.id === thinkingId
              ? {
                  ...m,
                  content: isAbort ? "" : `Fehler: ${err.message ?? "Unbekannter Fehler"}`,
                  isStreaming: false,
                  isAborted: isAbort,
                  thinkingDurationMs: durationMs,
                }
              : m
          ),
        }));
      });
  };

  render(): JSX.Element {
    const { title, isOfficeInitialized } = this.props;
    const { draft, isSending, messages, selectedModelId, models, user, userSettings, webSearch, scrollTarget, openSidebar, proposalsMessageId, chatId, folders, activeFolderId, historyRefreshKey, currentChatTitle } = this.state;

    if (!isOfficeInitialized) {
      return <Progress title={title} logo="assets/logo-filled.png" message="Bitte Word öffnen und das Add-In laden." />;
    }

    const pmId = proposalsMessageId;
    const proposalsMsg = pmId ? messages.find((m) => m.id === pmId) : undefined;

    return (
      <div className="app-root">
        <div className="app-root__main">
          <AppShell
            draft={draft}
            isSending={isSending}
            messages={messages}
            quickActions={quickActions}
            model={this.currentModel}
            selectedModelId={selectedModelId}
            models={models}
            user={user}
            userSettings={userSettings}
            onModelSelect={this.handleModelSelect}
            onDraftChange={this.handleDraftChange}
            onQuickAction={this.handleQuickAction}
            onSend={this.handleSend}
            onSendText={this.handleSendText}
            onStop={this.handleStop}
            onNewChat={this.handleNewChat}
            webSearch={webSearch}
            onToggleWebSearch={this.handleToggleWebSearch}
            onSwitchBranch={this.handleSwitchBranch}
            onEditSave={this.handleEditSave}
            onEditResend={this.handleEditResend}
            onDeleteMessage={this.handleDeleteMessage}
            scrollTarget={scrollTarget}
            historyOpen={openSidebar === "history"}
            onToggleHistory={this.handleToggleHistory}
            onDockedChange={this.handleDockedChange}
            chatId={chatId}
            onSelectChat={this.handleSelectChat}
            folders={folders}
            activeFolderId={activeFolderId}
            onEnterFolder={this.handleEnterFolder}
            onClearProject={this.handleClearProject}
            onProjectCreated={this.handleProjectCreated}
            historyRefreshKey={historyRefreshKey}
            chatTitle={currentChatTitle}
            onMoveChat={this.handleMoveChat}
            onMoveFolder={this.handleMoveFolder}
            onOpenProposals={this.handleOpenProposals}
            proposalsOpen={openSidebar === "proposals"}
            proposals={proposalsMsg?.changeProposals ?? []}
            proposalStates={proposalsMsg?.changeStates ?? {}}
            applyMode={this.state.applyMode}
            onApplyModeChange={this.handleApplyModeChange}
            onAcceptProposal={(id): void => pmId && this.handleAcceptProposal(pmId, id)}
            onRejectProposal={(id): void => pmId && this.handleRejectProposal(pmId, id)}
            onAcceptAllProposals={(): void => pmId && this.handleAcceptAllProposals(pmId)}
            onRejectAllProposals={(): void => pmId && this.handleRejectAllProposals(pmId)}
            onScrollToParagraph={this.handleScrollToParagraph}
            onCloseProposals={this.handleCloseSidebar}
          />
        </div>
        <ModalHost />
      </div>
    );
  }
}
